import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { AdminLoginPage, AdminReenrolPage, AdminSetupPage } from './pages/Auth.tsx';
import { AuditPage } from './pages/Audit.tsx';
import { AnnouncementsPage } from './pages/Announcements.tsx';
import { NotificationOpsPage, ProviderBoardPage } from './pages/Operations.tsx';
import { SupportInboxPage, SupportThreadPage } from './pages/support/Support.tsx';
import { DeskPage } from './pages/Desk.tsx';
import { WalletsPage } from './pages/Wallets.tsx';
import { HoldingsPage } from './pages/Holdings.tsx';
import { BanksPage } from './pages/Banks.tsx';
import { Customer360Page } from './pages/Customer360.tsx';
import { CustomersPage } from './pages/Customers.tsx';
import { KycQueuePage } from './pages/KycQueue.tsx';
import { OverviewPage } from './pages/Overview.tsx';
import { HopexBanksPage } from './pages/money/HopexBanks.tsx';
import { LedgerPage } from './pages/money/Ledger.tsx';
import { ReconciliationPage } from './pages/money/Reconciliation.tsx';
import { TomanDepositsPage } from './pages/money/TomanDeposits.tsx';
import { TomanWithdrawalsPage } from './pages/money/TomanWithdrawals.tsx';
import { NotFoundPage, SectionPage } from './pages/Section.tsx';
import { CataloguePage } from './pages/crypto/Catalogue.tsx';
import { CryptoDepositsPage } from './pages/crypto/CryptoDeposits.tsx';
import { CryptoWithdrawalsPage } from './pages/crypto/CryptoWithdrawals.tsx';
import { ScreeningPage } from './pages/crypto/Screening.tsx';
import { ConversionsPage } from './pages/trading/Conversions.tsx';
import { RatesPage } from './pages/trading/Rates.tsx';
import {
  ExportsPage,
  FinancialReportPage,
  ReferralAdminPage,
  SettingsPage,
} from './pages/Governance.tsx';
import { AdminGate } from './shell/AdminGate.tsx';
import { ADMIN_NAV } from './shell/nav.ts';

const root = createRootRoute({ component: Outlet, notFoundComponent: NotFoundPage });
/** Everything in the control room requires a full (non-restricted) admin session. */
const shell = createRoute({ getParentRoute: () => root, id: 'shell', component: AdminGate });

const loginRoute = createRoute({
  getParentRoute: () => root,
  path: '/login',
  component: AdminLoginPage,
});
const setupRoute = createRoute({
  getParentRoute: () => root,
  path: '/setup',
  component: AdminSetupPage,
});
const reenrolRoute = createRoute({
  getParentRoute: () => root,
  path: '/reenrol',
  component: AdminReenrolPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => shell,
  path: '/',
  component: OverviewPage,
});

/** Built sections; every other (future) section renders its placeholder. */
const LIVE: Record<string, () => React.JSX.Element> = {
  '/users': CustomersPage,
  '/kyc': KycQueuePage,
  '/audit': AuditPage,
  '/maintenance': ProviderBoardPage,
  '/user-banks': BanksPage,
  '/toman-deposits': TomanDepositsPage,
  '/toman-withdrawals': TomanWithdrawalsPage,
  '/adjustments': LedgerPage,
  '/reconciliation': ReconciliationPage,
  '/hopex-banks': HopexBanksPage,
  '/crypto-deposits': CryptoDepositsPage,
  '/crypto-withdrawals': CryptoWithdrawalsPage,
  '/assets': CataloguePage,
  '/screening': ScreeningPage,
  '/conversions': ConversionsPage,
  '/rates': RatesPage,
  '/support': SupportInboxPage,
  '/desk': DeskPage,
  '/wallets': WalletsPage,
  '/holdings': HoldingsPage,
  '/announcements': AnnouncementsPage,
  '/notifications': NotificationOpsPage,
  '/reports': FinancialReportPage,
  '/exports': ExportsPage,
  '/referral': ReferralAdminPage,
  '/settings': SettingsPage,
};

// Section routes are generated from the navigation table so menu and routes can never drift.
const sectionRoutes = ADMIN_NAV.flatMap((g) => g.items)
  .filter((i) => i.to !== '/')
  .map((item) =>
    createRoute({
      getParentRoute: () => shell,
      path: item.to,
      component:
        LIVE[item.to] ??
        function Section() {
          return <SectionPage item={item} />;
        },
    }),
  );

/** Customer 360: `?tab=` deep-links a tab; `?fixture=` exists only in development builds. */
const userDetailRoute = createRoute({
  getParentRoute: () => shell,
  path: '/users/$id',
  validateSearch: (s: Record<string, unknown>): { tab?: string; fixture?: string } => ({
    ...(typeof s.tab === 'string' ? { tab: s.tab } : {}),
    ...(typeof s.fixture === 'string' ? { fixture: s.fixture } : {}),
  }),
  component: function UserDetail() {
    const { id } = userDetailRoute.useParams();
    const search = userDetailRoute.useSearch();
    const navigate = userDetailRoute.useNavigate();
    return (
      <Customer360Page
        key={id}
        id={id}
        tab={search.tab}
        onTab={(tab) =>
          void navigate({
            search: (s: { tab?: string; fixture?: string }) => ({ ...s, tab }),
            replace: true,
          })
        }
      />
    );
  },
});

const supportThreadRoute = createRoute({
  getParentRoute: () => shell,
  path: '/support/$id',
  component: function SupportThread() {
    const { id } = supportThreadRoute.useParams();
    return <SupportThreadPage key={id} id={id} />;
  },
});

export const router = createRouter({
  routeTree: root.addChildren([
    shell.addChildren([dashboardRoute, userDetailRoute, supportThreadRoute, ...sectionRoutes]),
    loginRoute,
    setupRoute,
    reenrolRoute,
  ]),
  defaultPreload: 'intent',
});
